
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useRoute, useRouter } from 'vue-router';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'offices-overview',
    components: {},
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const route = useRoute();
      const router = useRouter();
      const { can } = useAbility();
      const office = ref<any | null>(null);
      const selectedStatus = ref<boolean>();
      const selectedVerification = ref<boolean>();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const badges = ref<Array<any>>([]);
      const selectedBadgeId = ref<string | number>('');
      const thumbnail = ref('/media/avatars/blank-image.jpg');
      const payload = ref<any>(null);

      const selectedBadge = computed(() => {
        return badges.value.find((badge) => badge.id === selectedBadgeId.value);
      });

      let employees = ref<any[]>([]);
      let employeeCount = ref<number>(0);

      const translate = (text, options = {}) => {
        if (te(text)) {
          return t(text, options);
        } else {
          return text;
        }
      };

      const currentPage = ref(1);
      const pageSize = ref(10);
      const totalItems = ref(0);
      const isNext = ref(false);

      const fetchEmployees = async () => {
        const res = await store.dispatch(
          Actions.GET_EMPLOYEES,
          office.value.business_account_info.id
        );

        employees.value = res.data.data;
        employeeCount.value = res.data.totalItems;
        currentPage.value = res.data.currentPage;
        pageSize.value = res.data.pageSize;
        totalItems.value = res.data.totalItems;
        isNext.value = res.data.isNext;
      };

      const fetchBadges = async () => {
        try {
          badges.value = await store.dispatch(Actions.GET_ALL_BADGES);

          if (office.value.business_account_info.badgeId) {
            selectedBadgeId.value = office.value.business_account_info.badgeId;
          } else {
            const defaultId = badges.value.find(
              (badge) => badge.slug === 'RisingStar'
            ).id;
            selectedBadgeId.value = defaultId; //   by default set RisingStar is null
          }
          if (office.value.logo) thumbnail.value = office.value.logo;
        } catch (error) {
          console.error('Error loading office or badges:', error);
        }
      };

      const onPageChange = (direction: 'next' | 'previous') => {
        if (direction === 'next' && isNext.value) {
          currentPage.value += 1;
        } else if (direction === 'previous' && currentPage.value > 1) {
          currentPage.value -= 1;
        }
        fetchEmployees();
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('officeOverview'), [
          translate('offices'),
        ]);
        const { data } = await store.dispatch(
          Actions.GET_OFFICE,
          route.params.id
        );
        office.value = data.data;
        selectedStatus.value = office.value.is_active;
        selectedVerification.value = office.value.isVerifiedOffice;
        office.value.createdAt = new Date(
          office.value.createdAt
        ).toLocaleString();

        fetchBadges();
        fetchEmployees();
      });

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const goBack = () => {
        router.back();
      };

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        const badgeId =
          selectedBadgeId.value === '' || selectedBadgeId.value == null
            ? null
            : Number(selectedBadgeId.value);

        const payload: any = { badgeId };

        await store.dispatch(Actions.UPDATE_OFFICE, {
          id: route.params.id,
          data: payload,
        });

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_OFFICE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.go(0);
          });
        }
      };

      const officeSchema = Yup.object().shape({
        is_active: Yup.boolean().required(translate('STATUS_IS_REQUIRED')),
        isVerifiedOffice: Yup.boolean().required(
          translate('VERIFICATION_STATUS_IS_REQUIRED')
        ),
      });

      const toggledVerificationStatus = async (user) => {
        const deletedMessage = user.isVerifiedOffice
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');

          // loading.value = true;
          await store.dispatch(Actions.UPDATE_VERIFICATION, user.id);
          // await store.dispatch(Actions.GET_OFFICES);
          // loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            await store.dispatch(
              Actions.REMOVE_BODY_CLASSNAME,
              'page-loading-over'
            );
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            await store.dispatch(
              Actions.REMOVE_BODY_CLASSNAME,
              'page-loading-over'
            );
            const successMsg = user.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            }).then(() => {
              router.go(0);
            });
          }
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        toggledVerificationStatus,
        onSubmitUpdate,
        submitButton,
        selectedStatus,
        selectedVerification,
        office,
        translate,
        can,
        goBack,
        officeSchema,
        goTo,
        employees,
        fetchEmployees,
        onPageChange,
        currentPage,
        pageSize,
        totalItems,
        isNext,
        employeeCount,
        fetchBadges,
        badges,
        selectedBadgeId,
        selectedBadge,
        thumbnail,
        payload,
      };
    },
  });
